import React, { useState, useEffect, useCallback } from "react";
import { Search, X, GitCompare, Building2, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PredictionBadge } from "@/components/badges";
import TimelineView from "@/components/TimelineView";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function VoterPicker({ label, selected, onSelect, onClear, committee, role }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    const params = { search: term, page_size: 8 };
    if (role === "super_admin" && committee !== "all") params.committee_no = committee;
    const { data } = await api.get("/voters/paged", { params });
    setResults(data.items);
  }, [committee, role]);

  useEffect(() => {
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, search]);

  return (
    <div className="relative">
      <p className="text-sm font-medium text-foreground mb-2">{label}</p>
      {selected ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-3" data-testid={`picked-${label}`}>
          <Building2 className="h-4 w-4 text-cyan-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{selected.company_name}</p>
            <p className="text-xs text-muted-foreground">{selected.branch} · {selected.team_assignment_2026 || "-"}</p>
          </div>
          <StatusBadge value={selected.status} />
          <PredictionBadge value={selected.current_vote_prediction_2026} />
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClear} data-testid={`clear-${label}`}><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder="Firma ara (en az 2 harf)..." className="pl-9" data-testid={`search-${label}`} />
          {open && results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-64 overflow-y-auto">
              {results.map((v) => (
                <button key={v.id} onClick={() => { onSelect(v); setOpen(false); setQ(""); setResults([]); }}
                  className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b border-border/50 last:border-0" data-testid={`result-${v.id}`}>
                  <p className="font-medium truncate">{v.company_name}</p>
                  <p className="text-xs text-muted-foreground">{v.branch} · Kayıt {v.registration_year}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ComparisonTimeline() {
  const { committee, user } = useAuth();
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  const doExport = async () => {
    if (!a || !b) return;
    try {
      const res = await api.get("/voters/compare/export", { params: { a: a.id, b: b.id }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = "karsilastirma.xlsx";
      el.click();
      URL.revokeObjectURL(url);
      toast.success("Karşılaştırma Excel'e aktarıldı");
    } catch {
      toast.error("Dışarı aktarma başarısız");
    }
  };

  return (
    <div className="space-y-4" data-testid="comparison-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <GitCompare className="h-6 w-6 text-cyan-600" />Zaman Tüneli Karşılaştırma
          </h1>
          <p className="text-sm text-muted-foreground">İki firmanın 2022→2026 seyrini yan yana karşılaştırın</p>
        </div>
        <Button variant="outline" size="sm" disabled={!a || !b} onClick={doExport} data-testid="compare-export-button">
          <Download className="h-4 w-4 mr-2" />Excel'e Aktar
        </Button>
      </div>

      <Card className="p-5 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VoterPicker label="Firma A" selected={a} onSelect={setA} onClear={() => setA(null)} committee={committee} role={user.role} />
          <VoterPicker label="Firma B" selected={b} onSelect={setB} onClear={() => setB(null)} committee={committee} role={user.role} />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border border-border" data-testid="timeline-col-a">
          <h3 className="font-display font-semibold text-sm mb-2">{a ? a.company_name : "Firma A seçilmedi"}</h3>
          {a ? <TimelineView voterId={a.id} showIntro={false} /> : <p className="text-sm text-muted-foreground py-10 text-center">Yukarıdan bir firma seçin.</p>}
        </Card>
        <Card className="p-5 border border-border" data-testid="timeline-col-b">
          <h3 className="font-display font-semibold text-sm mb-2">{b ? b.company_name : "Firma B seçilmedi"}</h3>
          {b ? <TimelineView voterId={b.id} showIntro={false} /> : <p className="text-sm text-muted-foreground py-10 text-center">Yukarıdan bir firma seçin.</p>}
        </Card>
      </div>
    </div>
  );
}
